<template>
  <div>
    <div id="map" class="bg-gray-200"></div>
    <div v-if="!mapLoaded" class="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div class="p-4 text-center text-sm text-gray-600 bg-white/60 rounded flex items-center gap-2">
        <div class="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        지도 로딩 중
      </div>
    </div>
    <div class="hidden" v-for="post in posts" :key="post['.key']"></div>
  </div>
</template>

<script>
/* global naver */
import { firestore } from '@/firebase/firestore'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { mapMutations } from 'vuex'
import * as types from '@/vuex/mutation_types'
import submarkersData from '@/assets/data/submarkers.json'

export default {
  name: 'Map',
  data () {
    return {
      map: null,
      posts: [],
      markers: new Map(), // Use a Map to track markers by post key
      mapLoaded: false,
      submarkers: submarkersData
    }
  },
  methods: {
    ...mapMutations({
      setKey: types.SET_KEY,
      setTitle: types.SET_TITLE,
      setContent: types.SET_CONTENT,
      setYoutube: types.SET_YOUTUBE,
      setLat: types.SET_LAT,
      setLng: types.SET_LNG
    }),
    waitForNaver () {
      return new Promise((resolve, reject) => {
        const check = () => {
          if (typeof naver !== 'undefined' && naver.maps) {
            resolve()
          } else {
            setTimeout(check, 100)
          }
        }
        check()
        // Timeout after 10 seconds
        setTimeout(() => reject(new Error('Naver Maps load timeout')), 10000)
      })
    },
    async initMap () {
      try {
        await this.waitForNaver()

        const Bounds = new naver.maps.LatLngBounds(
          new naver.maps.LatLng(33.370383, 126.890368),
          new naver.maps.LatLng(33.401924, 126.915543)
        )

        const container = document.getElementById('map')
        const options = {
          center: new naver.maps.LatLng(33.3945, 126.9057),
          zoom: 16,
          maxZoom: 18,
          minZoom: 15,
          maxBounds: Bounds,
          zoomControl: false,
          zoomControlOptions: {
            style: naver.maps.ZoomControlStyle.SMALL,
            position: naver.maps.Position.TOP_RIGHT
          }
        }

        this.map = new naver.maps.Map(container, options)
        this.map.setMapTypeId(naver.maps.MapTypeId.SATELLITE)
        this.mapLoaded = true

        naver.maps.Event.addListener(this.map, 'click', () => {
          this.resetZoom()
        })

        this.renderSubmarkers()
        this.startListeningPosts()
      } catch (err) {
        console.error('Map initialization failed:', err)
      }
    },
    renderSubmarkers () {
      this.submarkers.forEach(sub => {
        const deg = (typeof sub.deg !== 'undefined') ? sub.deg : 0
        const content = `<span class="small submarker" style="transform:rotate(${deg}deg); cursor:inherit;">${sub.title}</span>`
        // eslint-disable-next-line no-new
        new naver.maps.Marker({
          map: this.map,
          position: new naver.maps.LatLng(sub.lat, sub.lng),
          title: sub.title,
          clickable: false,
          icon: { content }
        })
      })
    },
    startListeningPosts () {
      try {
        const col = collection(firestore, 'Post')
        const q = query(col, where('show', '==', true))
        onSnapshot(q, snapshot => {
          const currentKeys = new Set()

          snapshot.forEach(doc => {
            const data = doc.data()
            const key = doc.id
            data['.key'] = key
            currentKeys.add(key)

            if (!this.markers.has(key)) {
              this.createMarker(data)
            }
          })

          // Remove markers that are no longer in the snapshot
          for (const [key, marker] of this.markers.entries()) {
            if (!currentKeys.has(key)) {
              marker.setMap(null)
              this.markers.delete(key)
            }
          }
        })
      } catch (e) {
        console.warn('listenPosts error', e)
      }
    },
    createMarker (post) {
      if (!post || !this.map || !post.show) return

      const markerHtml = `<span style="pointer-events:auto;" class="marker">${post.title}</span>`

      const marker = new naver.maps.Marker({
        map: this.map,
        position: new naver.maps.LatLng(post.lat, post.lng),
        title: post.title,
        clickable: true,
        zIndex: 999,
        icon: {
          content: markerHtml,
          size: new naver.maps.Size(100, 100),
          anchor: new naver.maps.Point(50, 50)
        }
      })

      naver.maps.Event.addListener(marker, 'click', () => {
        this.handleMarkerClick(marker, post)
      })

      // Optimized DOM listener attachment
      setTimeout(() => {
        if (marker.eventTarget && marker.eventTarget.style) {
          marker.eventTarget.style.cursor = 'pointer'
        }
      }, 150)

      this.markers.set(post['.key'], marker)
    },
    handleMarkerClick (marker, post) {
      // Clear active class from other markers
      this.markers.forEach(m => {
        if (m.eventTarget && m.eventTarget.classList) {
          m.eventTarget.classList.remove('active-marker')
        }
      })
      if (marker.eventTarget && marker.eventTarget.classList) {
        marker.eventTarget.classList.add('active-marker')
      }

      // Center map slightly above marker
      this.map.panToBounds(new naver.maps.LatLngBounds(new naver.maps.LatLng(post.lat + 0.001, post.lng)))

      // Set store state
      this.setKey(post['.key'])
      this.setTitle(post.title)
      this.setContent(post.content)
      this.setYoutube(post.youtube)
      this.setLat(post.lat)
      this.setLng(post.lng)

      // Navigate to post
      this.$router.push({ name: 'post', params: { key: post['.key'] } }).catch(() => {})
    },
    resetZoom () {
      if (!this.map) return
      this.map.setZoom(16, true)
      this.markers.forEach(marker => {
        if (marker.eventTarget && marker.eventTarget.classList) {
          marker.eventTarget.classList.remove('active-marker')
        }
      })
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    }
  },
  watch: {
    '$route' (to) {
      if (to.path === '/') {
        this.resetZoom()
      }
    }
  },
  mounted () {
    this.initMap()
  }
}
</script>

<style scoped>
#map {
  width: 100% !important;
  height: 100vh !important;
  position: absolute !important;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
}

/* Spinner animation for loading */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
